import React, { useContext, useEffect, useMemo, useState } from "react";

import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/system";
import {
  coreDarkTheme,
  coreLightTheme,
  gravityDarkTheme,
  gravityLightTheme,
  miromaDarkTheme,
  miromaLightTheme,
  demoTheme,

  clientThemes,
  clientPalettes,
  clientFonts,
} from "./clients";
import {
  bupaTheme,
  tflTheme,
  rubiconTheme,
  bnqTheme,
  screwfixTheme,
  b365Theme
} from "./demos";

import { DemoContext, ThemeContext } from "./ThemeContext";

const THEME_MAP: Record<string, any> = {
  gravity: { dark: gravityDarkTheme, light: gravityLightTheme },
  miroma: { dark: miromaDarkTheme, light: miromaLightTheme },
  core: { dark: coreDarkTheme, light: coreLightTheme },
  demo: { dark: demoTheme, light: demoTheme },
  bupa: { dark: bupaTheme, light: bupaTheme },
  tfl: { dark: tflTheme, light: tflTheme },
  rubicon: { dark: rubiconTheme, light: rubiconTheme },
  bnq: { dark: bnqTheme, light: bnqTheme },
  screwfix: { dark: screwfixTheme, light: screwfixTheme },
  bet365: { dark: b365Theme, light: b365Theme },
};

type ClientKey = keyof typeof clientPalettes;

export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [darkMode, setDarkMode] = useState(true);

  const client = process.env.REACT_APP_CLIENT || "core";
  const isDemoMode = client === 'demo';


  // Demo Client
  const [demoClient, setDemoClient] = useState<string | undefined>(() => {
    return isDemoMode ? localStorage.getItem('demo-client') || undefined : undefined;
  });

  useEffect(() => {
    if (demoClient) {
      localStorage.setItem('demo-client', demoClient);
    } else {
      localStorage.removeItem('demo-client');
    }
  }, [demoClient])

  const createClientTheme = (
    client: ClientKey,
    mode: 'dark' | 'light' = 'dark'
  ) => {

    const baseTheme = clientThemes[client] || clientThemes.core;
    const palette = clientPalettes[client]?.[mode] || clientPalettes.core.dark;
    const font = clientFonts[client] || clientFonts.core;
    return createTheme({
      ...baseTheme,
      palette,
      typography: {
        ...font,
      },
    });
  }

  const theme = useMemo(() => {
    return createClientTheme(client as ClientKey, darkMode ? 'dark' : 'light');
  }, [client, darkMode]);

  // const getActiveConfig = () => {
  //   if (isDemoMode && demoClient && THEME_MAP[demoClient]) {
  //     return THEME_MAP[demoClient];
  //   }
  //   return THEME_MAP[client] || THEME_MAP.core;
  // };
  //
  // const configs = getActiveConfig();
  // const theme = useMemo(() => {
  //   return createTheme(darkMode ? configs.dark : configs.light)
  // }, [darkMode, configs]);



  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <DemoContext.Provider value={{ isDemoMode, demoClient, setDemoClient}}>
      <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </ThemeContext.Provider>
    </DemoContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error("useThemeControl must be used within a ThemeControlProvider");
  }
  return context;
}
